import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { assertPagePermission } from "@/features/auth/lib/assert-page-permission";
import { Sentry } from "@/lib/sentry";
import { ApiError, getLocalUserFacingApiMessage } from "@/lib/api-client";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { usePageState } from "@/hooks/usePageState";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { usePermissions } from "@/features/auth/hooks/usePermissions";
import {
  pageStateQueryOptions,
  PageAccessError,
  opportunitySetsQueryOptions,
  opportunityFacetsQueryOptions,
  opportunitySetKeys,
  activeTasksQueryOptions,
  revealGenerationTask,
  useRenameOpportunitySet,
  useDeleteOpportunitySet,
} from "@/lib/queries";
import { tenantSettingsKeys } from "@/lib/queries/tenant-settings.queries.js";
import {
  createOpportunitySet,
  retryOpportunitySet,
} from "@/features/opportunities/api/opportunity-sets.api";
import { taskProgressKeys } from "@/lib/queries/task-progress.queries";
import { dismissTask } from "@/features/task-progress/api/task-progress.api";
import { useTaskSectionJump } from "@/features/task-progress/hooks/useTaskSectionJump";
import { TaskNavigationSectionSchema, type TaskRecord } from "@nexus/contracts";
import { OpportunitiesPage as OpportunitiesFeature } from "@/features/opportunities/components/page/OpportunitiesPage/OpportunitiesPage";
import { OpportunitiesLoadingState } from "@/features/opportunities/components/page/OpportunitiesLoadingState/OpportunitiesLoadingState";
import { PageError, PageAccessErrorComponent } from "@/features/auth/components/PageError";
import type { Opportunity, OpportunitySet, OpportunityTabKey } from "@/features/opportunities/types/opportunities.types";
import type { OpportunitiesFilters } from "@/features/opportunities/components/table/OpportunitiesTableToolbar/OpportunitiesTableToolbar";
import {
  applyCreateToList,
  applyRetryToList,
  deriveErrorSetIds,
  deriveGeneratingSetIds,
  deriveGenerationErrors,
  deriveGenerationStartedAt,
} from "@/features/opportunities/lib/set-generation-state";
import {
  DEFAULT_OPPORTUNITY_FILTERS,
  DEFAULT_OPPORTUNITY_DATASET,
  INITIAL_OPPORTUNITY_FILTERS,
  OPPORTUNITY_FILTER_ALL,
  resolveAvailableDataset,
} from "@/features/opportunities/constants/opportunity-filters";
import { useGenerationTransitionEffects } from "@/features/opportunities/hooks/useGenerationTransitionEffects";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useTranslation } from "react-i18next";
import { trackOpportunitySetCreated, trackOpportunityExamplesDrawerOpened } from "@/lib/analytics-events";
import { defaultOpportunityTabForSet } from "@/features/opportunities/lib/default-opportunity-tab";

const TAB_VALUES: [OpportunityTabKey, ...OpportunityTabKey[]] = [
  "active",
  "completed_scripts",
  "orders",
  "rejected",
];

const ALL = "all" as const;

/** Query booleans arrive as strings; `z.coerce.boolean()` treats `"false"` as truthy. */
const booleanSearchParam = z
  .union([z.boolean(), z.enum(["true", "false"]).transform((v) => v === "true")])
  .optional();

const opportunitiesSearchSchema = z.object({
  setId: z.string().optional().catch(undefined),
  tab: z.enum(TAB_VALUES).catch("active"),
  q: z.string().catch(""),
  opportunityId: z.string().optional().catch(undefined),
  /** Script id to auto-expand its opportunity group + script row (deep link). */
  scriptId: z.string().optional().catch(undefined),
  /** Order id to auto-expand on the selected set's Orders tab. */
  orderId: z.string().optional().catch(undefined),
  /** Script id whose animatic storyboard modal is open — shareable link state. */
  storyboard: z.string().optional().catch(undefined),
  /** Deep link from Dashboard — auto-open Create Opportunity Set modal on mount. */
  createSet: booleanSearchParam.catch(undefined),
  /** Region a completed task card asked to jump to — see `TASK_NAVIGATION_SECTION`. */
  section: TaskNavigationSectionSchema.optional().catch(undefined),
  action: z.string().catch(ALL),
  dataset: z.string().optional(),
  flowName: z.string().optional(),
  feature: z.string().catch(ALL),
  subFeature: z.string().catch(ALL),
  engine: z.string().catch(ALL),
});

type OpportunitiesSearch = Omit<
  z.infer<typeof opportunitiesSearchSchema>,
  "dataset" | "flowName"
> & {
  dataset: string;
  flowName: string;
};

const defaultSearch: OpportunitiesSearch = {
  tab: "active",
  q: "",
  ...INITIAL_OPPORTUNITY_FILTERS,
  flowName: INITIAL_OPPORTUNITY_FILTERS.dataset,
};

/** Router `parseSearchWith(null)` coerces digit-only params to numbers — keep ids as strings for Zod + row matching. */
function normalizeOpportunitiesSearchInput(
  search: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...search };
  if (out.opportunityId != null) out.opportunityId = String(out.opportunityId);
  if (out.scriptId != null) out.scriptId = String(out.scriptId);
  if (out.orderId != null) out.orderId = String(out.orderId);
  if (out.storyboard != null) out.storyboard = String(out.storyboard);
  if (out.setId != null) out.setId = String(out.setId);
  return out;
}

function OpportunitiesErrorComponent({
  error,
  reset,
}: {
  error: Error;
  reset: () => void;
}) {
  const navigate = Route.useNavigate();
  const { t } = useTranslation();
  if (error instanceof PageAccessError) {
    return <PageAccessErrorComponent error={error} reset={reset} />;
  }
  return (
    <PageError
      onReset={() => void navigate({ to: "/opportunities", search: defaultSearch })}
      resetLabel={t("opportunities.goToOpportunities", "Go to Opportunities")}
    />
  );
}

function OpportunitiesPendingComponent() {
  // Read the search synchronously — the URL is already at the destination
  // when pendingComponent renders, so we know whether to show the sets grid
  // or a table skeleton.
  const search = Route.useSearch();
  return <OpportunitiesLoadingState variant={search.setId ? "table" : "sets"} />;
}

export const Route = createFileRoute("/_authenticated/opportunities")({
  beforeLoad: () => assertPagePermission("opportunities"),
  loader: ({ context }) => {
    const tenantId = useAuth.getState().activeTenantId;

    // Warm the sets cache in the background — component handles loading via useQuery.
    // `prefetchQuery` is fire-and-forget: it never throws, so a transient network
    // error or dev-pod timeout doesn't block navigation or trigger the error component.
    if (tenantId) {
      void context.queryClient.prefetchQuery(opportunitySetsQueryOptions(tenantId));
    }

    // Block only on the access check. Non-403 failures still throw into the
    // route's `errorComponent` so real backend outages aren't silently swallowed.
    return context.queryClient
      .ensureQueryData(pageStateQueryOptions("opportunities"))
      .catch((err: unknown) => {
        if (err instanceof PageAccessError) return null;
        // Transient errors (timeout, offline, network) are expected in dev/staging —
        // don't spam Sentry. Capture everything else (5xx crashes, parse errors, etc.).
        const isTransient =
          err instanceof ApiError &&
          (err.code === "TIMEOUT" || err.code === "NETWORK_ERROR" || err.code === "OFFLINE");
        if (!isTransient) {
          Sentry.captureException(err, { tags: { flow: "opportunities-loader" } });
        }
        throw err;
      });
  },
  component: OpportunitiesRoute,
  pendingComponent: OpportunitiesPendingComponent,
  errorComponent: OpportunitiesErrorComponent,
  validateSearch: (search: Record<string, unknown>): OpportunitiesSearch => {
    const result = opportunitiesSearchSchema.safeParse(
      normalizeOpportunitiesSearchInput(search),
    );
    if (!result.success) return defaultSearch;
    const coalesced = result.data.dataset ?? result.data.flowName;
    const dataset =
      coalesced == null || coalesced === ""
        ? DEFAULT_OPPORTUNITY_DATASET
        : coalesced;
    return { ...result.data, dataset, flowName: dataset };
  },
});

function OpportunitiesRoute() {
  const { t } = useTranslation();
  const { isLoading, error } = usePageState("opportunities");
  useDocumentTitle(t("page.title.opportunities"));
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const userEmail = useAuth((s) => s.user?.email);
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  useTaskSectionJump();

  const perms = usePermissions();
  const canList = perms.OPPORTUNITY_SETS_LIST.isOn;
  const canCreate = perms.OPPORTUNITY_SETS_CREATE.isOn;
  const canRename = perms.OPPORTUNITY_SETS_RENAME.isOn;
  const canDelete = perms.OPPORTUNITY_SETS_DELETE.isOn;
  const canRegen = perms.OPPORTUNITY_SETS_RETRY.isOn;
  const canFacets = perms.OPPORTUNITIES_FACETS.isOn;
  const canMinimalGeneration = perms.OPPORTUNITY_SETS_MINIMAL.isOn;

  const queryClient = useQueryClient();

  const {
    data: sets = [],
    isSuccess: setsLoaded,
    isLoading: setsLoading,
    isError: setsError,
  } = useQuery({
    ...opportunitySetsQueryOptions(activeTenantId),
    enabled: canList && !!activeTenantId,
  });

  const { data: activeTasks = [] } = useQuery({
    ...activeTasksQueryOptions(userEmail ?? undefined),
    enabled: !!userEmail,
  });

  // ─── Derived generation state ──────────────────────────────────────
  const generatingSetIds = React.useMemo(() => deriveGeneratingSetIds(sets), [sets]);
  const errorSetIds = React.useMemo(() => deriveErrorSetIds(sets), [sets]);
  const generationStartedAt = React.useMemo(() => deriveGenerationStartedAt(sets), [sets]);
  const generationErrorBySetId = React.useMemo(() => deriveGenerationErrors(sets), [sets]);

  const openSetFromToast = React.useCallback((setId: string) => {
    const set = sets.find((item) => item.id === setId) ?? null;
    void navigate({
      search: {
        setId,
        tab: defaultOpportunityTabForSet(set),
        q: "",
        opportunityId: undefined,
        scriptId: undefined,
        action: OPPORTUNITY_FILTER_ALL,
        dataset: DEFAULT_OPPORTUNITY_DATASET,
        flowName: DEFAULT_OPPORTUNITY_DATASET,
        feature: OPPORTUNITY_FILTER_ALL,
        subFeature: OPPORTUNITY_FILTER_ALL,
        engine: OPPORTUNITY_FILTER_ALL,
      },
    });
  }, [navigate, sets]);

  // Resolve the URL's setId against the fetched list so stale ids don't trigger
  // a 404 on /opportunities/facets or /opportunities/list.
  const resolvedSetId = search.setId && sets.some((s) => s.id === search.setId) ? search.setId : undefined;

  const previousSetIdRef = React.useRef<string | undefined>(undefined);
  React.useEffect(() => {
    if (!resolvedSetId) {
      previousSetIdRef.current = undefined;
      return;
    }
    if (previousSetIdRef.current === resolvedSetId) return;
    previousSetIdRef.current = resolvedSetId;

    const set = sets.find((item) => item.id === resolvedSetId);
    if (!set) return;

    const preferredTab = defaultOpportunityTabForSet(set);
    if (search.tab === "active" && preferredTab !== "active") {
      void navigate({
        search: (prev) => ({ ...prev, tab: preferredTab }),
        replace: true,
      });
    }
  }, [resolvedSetId, sets, navigate, search.tab]);

  const { data: facets } = useQuery({
    ...opportunityFacetsQueryOptions({
      setId: resolvedSetId,
      tab: search.tab,
      dataset: search.dataset,
    }),
    enabled: canFacets && !!resolvedSetId,
  });

  // Once sets have actually loaded, prune a stale `setId` from the URL so the
  // page drops back to the empty / sets-grid view.
  const navigateRef = React.useRef(navigate);
  navigateRef.current = navigate;
  React.useEffect(() => {
    if (!setsLoaded) return;
    if (!search.setId) return;
    if (sets.some((s) => s.id === search.setId)) return;
    void navigateRef.current({
      search: (prev) => ({ ...prev, setId: undefined, opportunityId: undefined, scriptId: undefined, storyboard: undefined }),
      replace: true,
    });
  }, [setsLoaded, sets, search.setId]);

  // A selected dataset with no rows in this set/tab (preselected Topics on a set without Topics,
  // stale Angles links) would render an empty table — widen to all datasets instead.
  const isOpportunityListTab = search.tab !== "completed_scripts" && search.tab !== "orders";
  React.useEffect(() => {
    if (!facets || !isOpportunityListTab) return;
    const dataset = resolveAvailableDataset(search.dataset, facets.datasets ?? facets.flowNames);
    if (dataset === search.dataset) return;
    void navigateRef.current({
      search: (prev) => ({
        ...prev,
        dataset,
        flowName: dataset,
        feature: OPPORTUNITY_FILTER_ALL,
        subFeature: OPPORTUNITY_FILTER_ALL,
      }),
      replace: true,
    });
  }, [facets, isOpportunityListTab, search.dataset]);

  // `initialFilters` preselects Topics on set switch; `clearFilters` resets to all-`all`.
  const initialFilters = INITIAL_OPPORTUNITY_FILTERS;
  const clearFilters = DEFAULT_OPPORTUNITY_FILTERS;

  const renameSet = useRenameOpportunitySet();
  const deleteSet = useDeleteOpportunitySet();

  const createSet = useMutation({
    mutationFn: (params: Parameters<typeof createOpportunitySet>[0]) => createOpportunitySet(params),
    onSuccess: (created, variables) => {
      // Show the generation task in the sidebar immediately (independent of tenant resolution).
      revealGenerationTask(queryClient);
      if (!activeTenantId) return;

      trackOpportunitySetCreated({
        opportunity_set_id: created.id,
        opportunity_set_name: created.name,
        industry: created.industry ?? variables.industry,
        products: variables.products?.join(', '),
        ad_platforms: created.adPlatforms ?? undefined,
        strategic_priorities: variables.strategicPriorities?.focusOn?.join(', '),
        brand_voice: variables.brandVoice?.alwaysSay,
        creation_source: 'overview_page',
      });

      // Splice the newly-created row in optimistically so the generating card
      // paints immediately; the background refetch reconciles any server-side
      // normalization (name suffixing, etc.).
      queryClient.setQueryData<OpportunitySet[]>(
        opportunitySetKeys.list(activeTenantId),
        (prev) => applyCreateToList(prev, created),
      );
      void queryClient.invalidateQueries({ queryKey: opportunitySetKeys.list(activeTenantId) });
      void queryClient.invalidateQueries({ queryKey: opportunitySetKeys.productTypes("opportunities", activeTenantId) });
      void queryClient.invalidateQueries({ queryKey: tenantSettingsKeys.settings(activeTenantId) });
      void queryClient.invalidateQueries({ queryKey: tenantSettingsKeys.competitors(activeTenantId) });
    },
    onError: (err: unknown) => {
      const description = getLocalUserFacingApiMessage(err);
      if (!description) return;
      toast.error(t("opportunities.toast.createFailed"), { description });
    },
    meta: { skipGlobalError: true },
  });

  const retrySet = useMutation({
    mutationFn: ({ setId, industry }: { setId: string; industry?: string }) =>
      retryOpportunitySet(setId, industry ? { industry } : {}),
    onSuccess: (updated) => {
      // Dismiss the old FAILED task card from the sidebar. The card persists until
      // explicitly removed (the worker only calls removeTask on cancel, not on failure).
      // Without this, the user sees two cards — the old Failed one and the new Generating one.
      const email = useAuth.getState().user?.email;
      if (email) {
        const taskData = queryClient.getQueryData<TaskRecord[]>(taskProgressKeys.active(email));
        const oldTask = taskData?.find((t) => t.navigation?.params?.setId === updated.replacedSetId);
        if (oldTask) {
          queryClient.setQueryData<TaskRecord[]>(
            taskProgressKeys.active(email),
            (prev) => prev?.filter((t) => t.correlationId !== oldTask.correlationId),
          );
          void dismissTask(oldTask.correlationId).catch(() => {});
        }
      }

      revealGenerationTask(queryClient);
      if (!activeTenantId) return;
      queryClient.setQueryData<OpportunitySet[]>(
        opportunitySetKeys.list(activeTenantId),
        (prev) => applyRetryToList(prev, updated) ?? prev,
      );
      void queryClient.invalidateQueries({ queryKey: opportunitySetKeys.list(activeTenantId) });
    },
    onError: () => {
      toast.error(t("opportunities.toast.retryFailed"), {
        description: t("opportunities.toast.generationFailedDescription"),
      });
    },
    meta: { skipGlobalError: true },
  });

  const handleRetrySet = React.useCallback((setId: string) => {
    const set = sets.find((s) => s.id === setId);
    retrySet.mutate({ setId, industry: set?.industry ?? undefined });
  }, [sets, retrySet]);

  const handleRegenSet = React.useCallback((setId: string) => {
    handleRetrySet(setId);
    void navigate({
      search: {
        setId: undefined,
        tab: "active",
        q: "",
        opportunityId: undefined,
        scriptId: undefined,
        action: OPPORTUNITY_FILTER_ALL,
        dataset: DEFAULT_OPPORTUNITY_DATASET,
        flowName: DEFAULT_OPPORTUNITY_DATASET,
        feature: OPPORTUNITY_FILTER_ALL,
        subFeature: OPPORTUNITY_FILTER_ALL,
        engine: OPPORTUNITY_FILTER_ALL,
      },
    });
  }, [handleRetrySet, navigate]);

  const handleToastDeleteSet = React.useCallback((id: string) => {
    deleteSet.mutate(id, {
      onSuccess: () => {
        if (search.setId === id) {
          const remaining = sets.filter((s) => s.id !== id);
          void navigate({
            search: {
              setId: remaining[0]?.id,
              tab: "active",
              q: "",
              opportunityId: undefined,
              scriptId: undefined,
              action: OPPORTUNITY_FILTER_ALL,
        dataset: DEFAULT_OPPORTUNITY_DATASET,
        flowName: DEFAULT_OPPORTUNITY_DATASET,
        feature: OPPORTUNITY_FILTER_ALL,
        subFeature: OPPORTUNITY_FILTER_ALL,
        engine: OPPORTUNITY_FILTER_ALL,
            },
          });
        }
      },
    });
  }, [deleteSet, search.setId, sets, navigate]);

  // `justCompleted` pulses the card for a beat after GENERATING → READY.
  // Persistent toasts for FAILED + empty-result transitions are also fired here.
  const { justCompletedSetIds } = useGenerationTransitionEffects({
    sets,
    activeTasks,
    onOpenSet: openSetFromToast,
    onRetrySet: handleRetrySet,
    onDeleteSet: handleToastDeleteSet,
  });

  // Snapshot the clicked opportunity so the drawer renders immediately with the
  // row's data rather than waiting for `infiniteData.find()` to resolve. Without
  // this, a transient `find() === undefined` (e.g. while react-query reconciles
  // pages on `keepPreviousData` swaps) flips `open` false→true, animating the
  // drawer closed-then-open on click. Falls back to find-from-data for direct
  // URL hits (bookmark / refresh) where no click happened.
  const [clickedOpportunity, setClickedOpportunity] = React.useState<Opportunity | null>(null);
  React.useEffect(() => {
    if (!search.opportunityId) setClickedOpportunity(null);
  }, [search.opportunityId]);

  if (isLoading || setsLoading) {
    return (
      <OpportunitiesLoadingState
        variant={search.setId ? "table" : "sets"}
      />
    );
  }

  if (error || setsError) {
    return <PageError is403={error instanceof PageAccessError} />;
  }

  const selectedSet =
    sets.find((s) => s.id === search.setId) ?? null;

  const filters: OpportunitiesFilters = {
    action: search.action,
    dataset: search.dataset,
    feature: search.feature,
    subFeature: search.subFeature,
    engine: search.engine,
  };

  return (
    <OpportunitiesFeature
      tenantId={activeTenantId ?? ""}
      sets={sets}
      selectedSet={selectedSet}
      activeTab={search.tab}
      search={search.q}
      filters={filters}
      facets={facets}
      showFacetFilters={canFacets}
      selectedOpportunityId={search.opportunityId}
      highlightScriptId={search.scriptId ?? search.storyboard}
      highlightOrderId={search.orderId}
      revealTaskTarget={Boolean(search.section)}
      openCreateSetOnMount={search.createSet === true && !search.setId}
      onCreateSetDialogOpened={() => {
        if (!search.createSet) return;
        void navigate({
          search: (prev) => ({ ...prev, createSet: undefined }),
          replace: true,
        });
      }}
      onSetChange={(set) => {
        void navigate({
          search: {
            setId: set?.id,
            tab: defaultOpportunityTabForSet(set),
            q: "",
            opportunityId: undefined,
            scriptId: undefined,
            ...initialFilters,
            flowName: initialFilters.dataset,
          },
        });
      }}
      canListSets={canList}
      canCreateSet={canCreate}
      canRenameSet={canRename}
      canDeleteSet={canDelete}
      isCreatingSet={createSet.isPending}
      generatingSetIds={generatingSetIds}
      generationStartedAt={generationStartedAt}
      justCompletedSetIds={justCompletedSetIds}
      errorSetIds={errorSetIds}
      generationErrorBySetId={undefined}
      onRetrySet={handleRetrySet}
      canRegenSet={canRegen}
      onRegenSet={handleRegenSet}
      onCreateSet={(values) => createSet.mutate({
        name: values.name,
        industry: values.industry,
        benchmarkIndustry: values.benchmarkIndustry,
        myBrand: values.brand ?? undefined,
        mainCompetitor: values.mainCompetitor ?? undefined,
        industryCompetitors: values.industryCompetitors.length > 0 ? values.industryCompetitors : undefined,
        products: values.products.length > 0 ? values.products : undefined,
        adPlatforms: values.adPlatforms ?? undefined,
        strategicPriorities: (values.strategicPriorities.focusOn.length > 0 || values.strategicPriorities.avoid.length > 0) ? values.strategicPriorities : undefined,
        brandVoice: (values.brandVoice.alwaysSay || values.brandVoice.neverSay) ? values.brandVoice : undefined,
        scriptGuidelines: values.scriptGuidelines || undefined,
        minimalGeneration: canMinimalGeneration && values.minimalGeneration ? true : undefined,
        startDate: values.startDate,
        endDate: values.endDate,
      })}
      onRenameSet={(id, name) => {
        renameSet.mutate({ id, name });
      }}
      isRenamingSet={renameSet.isPending}
      onDeleteSet={(id) => {
        deleteSet.mutate(id, {
          onSuccess: () => {
            if (search.setId === id) {
              const remaining = sets.filter((s) => s.id !== id);
              void navigate({
                search: {
                  setId: remaining[0]?.id,
                  tab: "active",
                  q: "",
                  opportunityId: undefined,
                  scriptId: undefined,
                  ...initialFilters,
                  flowName: initialFilters.dataset,
                },
              });
            }
          },
        });
      }}
      isDeletingSet={deleteSet.isPending}
      onTabChange={(tab) => {
        void navigate({
          search: (prev) => ({
            ...prev,
            tab,
            opportunityId: undefined,
            scriptId: undefined,
            orderId: undefined,
            storyboard: undefined,
            ...initialFilters,
            flowName: initialFilters.dataset,
          }),
        });
      }}
      onSearchChange={(q) => {
        void navigate({
          search: (prev) => ({ ...prev, q }),
          replace: true,
        });
      }}
      onFilterChange={(key, value) => {
        void navigate({
          search: (prev) => ({
            ...prev,
            [key]: value,
            ...(key === "dataset" && {
              flowName: value,
              feature: OPPORTUNITY_FILTER_ALL,
              subFeature: OPPORTUNITY_FILTER_ALL,
            }),
          }),
          replace: true,
        });
      }}
      onClearFilters={() => {
        void navigate({
          search: (prev) => ({
            ...prev,
            ...clearFilters,
            flowName: clearFilters.dataset,
          }),
          replace: true,
        });
      }}
      drawerOpportunity={clickedOpportunity}
      onDrawerOpen={(opp) => {
        setClickedOpportunity(opp);
        void navigate({
          search: (prev) => ({ ...prev, opportunityId: opp.id }),
        });

        trackOpportunityExamplesDrawerOpened({
          opportunity_id: opp.id,
          opportunity_set_id: search.setId ?? '',
          topic_title: opp.topicTitle ?? '',
          tab_context: search.tab ?? 'active',
        });
      }}
      onDrawerClose={() => {
        void navigate({
          search: (prev) => ({ ...prev, opportunityId: undefined }),
        });
      }}
    />
  );
}
